import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDateTime, label } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { Empty, Field, PageHeader } from "../../components/common/UI";
import { fromLocalInput, useLookups } from "../../components/common/Pickers";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const STATUS = { scheduled: "bg-sky-50 text-sky-800", in_progress: "bg-amber-50 text-amber-800", adjourned: "bg-emerald-50 text-emerald-800", cancelled: "bg-slate-100 text-slate-500" };
export const SessionStatus = ({ s }) => <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${STATUS[s]}`} data-testid="session-status">{label(s)}</span>;

function NewSession({ lk, onClose, onSaved }) {
  const cur = lk.terms.find((t) => t.is_current) || lk.terms[0];
  const [f, setF] = useState({ term_id: cur?.id, session_type: "regular", session_no: "", title: "", scheduled_at: "", venue: "", quorum_rule: "majority", quorum_custom: "", presiding_legislator_id: "" });
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      await api.post("/sessions", { ...f, session_no: Number(f.session_no), scheduled_at: fromLocalInput(f.scheduled_at), quorum_custom: f.quorum_custom ? Number(f.quorum_custom) : null, presiding_legislator_id: f.presiding_legislator_id || null, venue: f.venue || null });
      toast.success("Session scheduled");
      onClose();
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="session-dialog">
        <DialogHeader><DialogTitle>Schedule session</DialogTitle></DialogHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Term"><select className="otms-input" value={f.term_id} onChange={up("term_id")}>{lk.terms.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></Field>
          <Field label="Type"><select className="otms-input" value={f.session_type} onChange={up("session_type")} data-testid="session-type">{["regular", "special"].map((t) => <option key={t} value={t}>{label(t)}</option>)}</select></Field>
          <Field label="Session no."><input type="number" className="otms-input" value={f.session_no} onChange={up("session_no")} data-testid="session-no" /></Field>
          <Field label="Date and time (Asia/Manila)"><input type="datetime-local" className="otms-input" value={f.scheduled_at} onChange={up("scheduled_at")} data-testid="session-datetime" /></Field>
          <Field label="Title" className="md:col-span-2"><input className="otms-input" placeholder="e.g., 12th Regular Session" value={f.title} onChange={up("title")} data-testid="session-title" /></Field>
          <Field label="Venue"><input className="otms-input" value={f.venue} onChange={up("venue")} /></Field>
          <Field label="Presiding officer"><select className="otms-input" value={f.presiding_legislator_id} onChange={up("presiding_legislator_id")}><option value="">—</option>{lk.legislators.map((l) => <option key={l.id} value={l.id}>{l.full_name}</option>)}</select></Field>
          <Field label="Quorum rule"><select className="otms-input" value={f.quorum_rule} onChange={up("quorum_rule")}><option value="majority">Majority of members</option><option value="custom">Custom number</option></select></Field>
          {f.quorum_rule === "custom" && <Field label="Quorum (members)"><input type="number" className="otms-input" value={f.quorum_custom} onChange={up("quorum_custom")} /></Field>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-sky-600 hover:bg-sky-700" onClick={save} data-testid="session-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Sessions() {
  const { can } = useAuth();
  const lk = useLookups();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [params] = useSearchParams();
  const tab = params.get("tab") ? `?tab=${params.get("tab")}` : "";
  const load = useCallback(() => api.get("/sessions").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const months = rows.reduce((acc, s) => {
    const k = new Date(s.scheduled_at).toLocaleDateString("en-PH", { timeZone: "Asia/Manila", month: "long", year: "numeric" });
    (acc[k] = acc[k] || []).push(s);
    return acc;
  }, {});
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Sessions" title="Session Calendar" subtitle="Open a session to build its agenda, record attendance and quorum, motions, roll-call votes, minutes and journal."
        actions={can("session.manage") && <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => setOpen(true)} data-testid="add-session-button"><Plus className="mr-1 h-4 w-4" />Schedule session</Button>} />
      {rows.length === 0 && <Empty text="No sessions scheduled yet." />}
      <div className="space-y-6">
        {Object.entries(months).map(([m, list]) => (
          <section key={m}>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{m}</h2>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {list.map((s) => (
                <Link key={s.id} to={`/sessions/${s.id}${tab}`} className="rounded-md border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:border-sky-300 hover:shadow" data-testid={`session-card-${s.id}`}>
                  <div className="flex items-center justify-between"><span className="text-[11px] uppercase text-slate-500">{label(s.session_type)} · No. {s.session_no}</span><SessionStatus s={s.status} /></div>
                  <div className="mt-1 font-semibold text-slate-900">{s.title}</div>
                  <div className="mt-1 text-xs text-slate-500">{fmtDateTime(s.scheduled_at)}{s.venue ? ` · ${s.venue}` : ""} · {s.agenda_count} agenda items</div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
      {open && lk && <NewSession lk={lk} onClose={() => setOpen(false)} onSaved={load} />}
    </div>
  );
}
